import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { BACKEND_URL } from '../config/canonical';
import {
  Building2, Users, FolderOpen, UserCheck, RefreshCw, TrendingUp,
  ArrowRight, Activity, Shield
} from 'lucide-react';

const MainHubDashboard = () => {
  const token = localStorage.getItem('nexus_main_hub_token');
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, companiesRes, usersRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/main-hub/stats`, { headers }),
        fetch(`${BACKEND_URL}/api/main-hub/companies`, { headers }),
        fetch(`${BACKEND_URL}/api/main-hub/users`, { headers })
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (companiesRes.ok) setCompanies(await companiesRes.json());
      if (usersRes.ok) {
        const users = await usersRes.json();
        setRecentUsers(users.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 8));
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const statCards = [
    { label: 'Total Companies', value: stats?.total_companies || 0, icon: Building2, color: 'text-blue-400', bg: 'bg-blue-500/10' },
    { label: 'Total Users', value: stats?.total_users || 0, icon: Users, color: 'text-green-400', bg: 'bg-green-500/10' },
    { label: 'Total Cases', value: stats?.total_cases || 0, icon: FolderOpen, color: 'text-amber-400', bg: 'bg-amber-500/10' },
    { label: 'Total Clients', value: stats?.total_clients || 0, icon: UserCheck, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  ];

  const roleBadge = (role) => {
    const colors = {
      department_admin: 'bg-amber-500', field_investigator: 'bg-blue-500',
      skip_tracer: 'bg-purple-500', transporter: 'bg-cyan-500', client: 'bg-green-500'
    };
    return <Badge className={`${colors[role] || 'bg-slate-500'} text-white text-xs`}>{role?.replace(/_/g, ' ')}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="main-hub-dashboard">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Shield className="w-5 h-5 text-red-400" />
            <h1 className="text-2xl font-bold text-slate-900">Main Hub Dashboard</h1>
          </div>
          <p className="text-slate-400">Platform-wide overview and management</p>
        </div>
        <Button variant="outline" className="border-slate-300 text-slate-300" onClick={fetchData}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Card key={stat.label} className="bg-white border-slate-200" data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-400">{stat.label}</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stat.value}</p>
                </div>
                <div className={`w-10 h-10 ${stat.bg} rounded-lg flex items-center justify-center`}>
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/main-hub/companies">
          <Card className="bg-white border-slate-200 hover:border-blue-500/40 transition-colors cursor-pointer" data-testid="quick-action-companies">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-blue-400" />
                <span className="text-white font-medium">Manage Companies</span>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-500" />
            </CardContent>
          </Card>
        </Link>
        <Link to="/main-hub/users">
          <Card className="bg-white border-slate-200 hover:border-green-500/40 transition-colors cursor-pointer" data-testid="quick-action-users">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-green-400" />
                <span className="text-white font-medium">Manage Users</span>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-500" />
            </CardContent>
          </Card>
        </Link>
        <Card className="bg-white border-slate-200" data-testid="quick-action-activity">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Activity className="w-5 h-5 text-amber-400" />
              <span className="text-white font-medium">Platform Activity</span>
            </div>
            <Badge className="bg-green-500/20 text-green-400 text-xs">Live</Badge>
          </CardContent>
        </Card>
      </div>

      {/* Companies & Recent Users */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Companies Overview */}
        <Card className="bg-white border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Building2 className="w-4 h-4 text-blue-400" /> Companies
            </CardTitle>
            <Link to="/main-hub/companies" className="text-xs text-blue-400 hover:text-blue-300">View All</Link>
          </CardHeader>
          <CardContent className="space-y-3">
            {companies.length === 0 ? (
              <p className="text-slate-500 text-sm py-4 text-center">No companies registered</p>
            ) : (
              companies.slice(0, 5).map(company => (
                <div key={company.id} className="flex items-center justify-between py-2 border-b border-slate-700/50 last:border-0" data-testid={`company-row-${company.id}`}>
                  <div>
                    <p className="text-white text-sm font-medium">{company.name}</p>
                    <p className="text-xs text-slate-500">{company.contact_email}</p>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-slate-400"><Users className="w-3 h-3 inline mr-1" />{company.user_count || 0}</span>
                    <span className="text-slate-400"><FolderOpen className="w-3 h-3 inline mr-1" />{company.case_count || 0}</span>
                    <Badge className={`${company.status === 'ACTIVE' ? 'bg-green-500' : 'bg-slate-500'} text-white text-xs`}>
                      {company.status}
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Recent Users */}
        <Card className="bg-white border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-green-400" /> Recent Users
            </CardTitle>
            <Link to="/main-hub/users" className="text-xs text-green-400 hover:text-green-300">View All</Link>
          </CardHeader>
          <CardContent className="space-y-3">
            {recentUsers.length === 0 ? (
              <p className="text-slate-500 text-sm py-4 text-center">No users found</p>
            ) : (
              recentUsers.map(u => (
                <div key={u.id} className="flex items-center justify-between py-2 border-b border-slate-700/50 last:border-0" data-testid={`user-row-${u.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
                      <span className="text-slate-300 text-xs font-bold">{u.name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{u.name}</p>
                      <p className="text-xs text-slate-500">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {roleBadge(u.role)}
                    <Badge className={`${u.status === 'ACTIVE' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'} text-xs`}>
                      {u.status}
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MainHubDashboard;
